import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { 
  User, Users, Briefcase, Megaphone, Lightbulb, MapPin, 
  Handshake, Wallet, Heart, ChevronDown
} from 'lucide-react';

const OrganigramPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();

  const t = {
    fr: {
      title: "Organigramme de Direction",
      subtitle: "Alliance Démocratique Africaine",
      description: "Une structure claire et efficace pour porter le projet de la 4ᵉ République",
      firstCircle: "Le Premier Cercle",
      firstCircleDesc: "Direction stratégique et coordination",
      fivePoles: "Les Cinq Pôles Fonctionnels",
      fivePolesDesc: "Sous l'autorité du Secrétaire Général",
      reportsTo: "Rattaché au",
      coordinates: "Coordonne les pôles"
    },
    en: {
      title: "Leadership Organization Chart",
      subtitle: "African Democratic Alliance",
      description: "A clear and effective structure to carry the 4th Republic project",
      firstCircle: "The First Circle",
      firstCircleDesc: "Strategic direction and coordination",
      fivePoles: "The Five Functional Poles",
      fivePolesDesc: "Under the authority of the Secretary General",
      reportsTo: "Reports to",
      coordinates: "Coordinates the poles"
    },
    pt: {
      title: "Organigrama de Direção",
      subtitle: "Aliança Democrática Africana",
      description: "Uma estrutura clara e eficaz para levar o projeto da 4ª República",
      firstCircle: "O Primeiro Círculo",
      firstCircleDesc: "Direção estratégica e coordenação",
      fivePoles: "Os Cinco Polos Funcionais",
      fivePolesDesc: "Sob a autoridade do Secretário-Geral",
      reportsTo: "Reporta ao",
      coordinates: "Coordena os polos"
    }
  };

  const content = t[language] || t.fr;

  // President
  const president = {
    name: "ALPHA O. DIALLO",
    role: {
      fr: "Président",
      en: "President",
      pt: "Presidente"
    },
    desc: {
      fr: "Direction politique et arbitrages majeurs",
      en: "Political direction and major arbitrations",
      pt: "Direção política e arbitragens principais"
    }
  };

  // First Circle - Direct reports to President
  const firstCircle = [
    {
      id: 'cabinet',
      name: null, // Position to be filled
      role: {
        fr: "Directeur de Cabinet",
        en: "Chief of Staff",
        pt: "Diretor de Gabinete"
      },
      desc: {
        fr: "Agenda, arbitrages, confidentialité, exécution des décisions présidentielles",
        en: "Agenda, arbitrations, confidentiality, execution of presidential decisions",
        pt: "Agenda, arbitragens, confidencialidade, execução das decisões presidenciais"
      },
      reportsTo: 'president',
      icon: Briefcase,
      color: '#5B2D8E'
    },
    {
      id: 'sg',
      name: null, // Position to be filled
      role: {
        fr: "Secrétaire Général",
        en: "Secretary General",
        pt: "Secretário-Geral"
      },
      desc: {
        fr: "Colonne vertébrale de l'appareil. Coordonne les 5 pôles, structure le parti, anime fédérations et sections",
        en: "Backbone of the organization. Coordinates the 5 poles, structures the party, animates federations and sections",
        pt: "Espinha dorsal da organização. Coordena os 5 polos, estrutura o partido, anima federações e secções"
      },
      reportsTo: 'president',
      icon: Users,
      color: '#F7C948',
      isCoordinator: true
    },
    {
      id: 'comm',
      name: null, // Position to be filled
      role: {
        fr: "Directeur Communication & Numérique",
        en: "Director of Communication & Digital",
        pt: "Diretor de Comunicação & Digital"
      },
      desc: {
        fr: "Messages, presse, réseaux sociaux, production de contenus, identité visuelle",
        en: "Messages, press, social networks, content production, visual identity",
        pt: "Mensagens, imprensa, redes sociais, produção de conteúdos, identidade visual"
      },
      reportsTo: 'sg',
      icon: Megaphone,
      color: '#0D9488'
    }
  ];

  // Five Functional Poles
  const poles = [
    {
      id: 'ideas',
      name: null, // Position to be filled
      role: {
        fr: "Directeur Exécutif aux Idées & au Programme",
        en: "Executive Director of Ideas & Program",
        pt: "Diretor Executivo de Ideias & Programa"
      },
      shortRole: {
        fr: "Idées & Programme",
        en: "Ideas & Program",
        pt: "Ideias & Programa"
      },
      desc: {
        fr: "Notes techniques, propositions, veille sur les politiques publiques",
        en: "Technical notes, proposals, monitoring public policies",
        pt: "Notas técnicas, propostas, monitoramento de políticas públicas"
      },
      icon: Lightbulb,
      color: '#5B2D8E'
    },
    {
      id: 'mobilisation',
      name: "FRANÇOIS N. KOUAKOU",
      role: {
        fr: "Directeur Exécutif à la Mobilisation & au Terrain",
        en: "Executive Director of Mobilization & Field",
        pt: "Diretor Executivo de Mobilização & Terreno"
      },
      shortRole: {
        fr: "Mobilisation & Terrain",
        en: "Mobilization & Field",
        pt: "Mobilização & Terreno"
      },
      desc: {
        fr: "Implantation, militants, événements, relais locaux",
        en: "Implementation, activists, events, local relays",
        pt: "Implantação, militantes, eventos, relés locais"
      },
      icon: MapPin,
      color: '#F7C948'
    },
    {
      id: 'alliances',
      name: null, // Position to be filled
      role: {
        fr: "Directeur Exécutif aux Alliances & Relations",
        en: "Executive Director of Alliances & Relations",
        pt: "Diretor Executivo de Alianças & Relações"
      },
      shortRole: {
        fr: "Alliances & Relations",
        en: "Alliances & Relations",
        pt: "Alianças & Relações"
      },
      desc: {
        fr: "Dialogue avec autres forces politiques, société civile, partenaires",
        en: "Dialogue with other political forces, civil society, partners",
        pt: "Diálogo com outras forças políticas, sociedade civil, parceiros"
      },
      icon: Handshake,
      color: '#0D9488'
    },
    {
      id: 'finances',
      name: "EDWIGE L. KOUASSI LENOIR",
      role: {
        fr: "Directeur Exécutif aux Finances & à la Logistique",
        en: "Executive Director of Finance & Logistics",
        pt: "Diretor Executivo de Finanças & Logística"
      },
      shortRole: {
        fr: "Finances & Logistique",
        en: "Finance & Logistics",
        pt: "Finanças & Logística"
      },
      desc: {
        fr: "Ressources, transparence financière, moyens matériels",
        en: "Resources, financial transparency, material means",
        pt: "Recursos, transparência financeira, meios materiais"
      },
      icon: Wallet,
      color: '#5B2D8E'
    },
    {
      id: 'generations',
      name: "DORYANE J. DIALLO",
      role: {
        fr: "Directeur Exécutif aux Générations & Solidarités",
        en: "Executive Director of Generations & Solidarity",
        pt: "Diretor Executivo de Gerações & Solidariedades"
      },
      shortRole: {
        fr: "Générations & Solidarités",
        en: "Generations & Solidarity",
        pt: "Gerações & Solidariedades"
      },
      desc: {
        fr: "Jeunesse, seniors, liens intergénérationnels",
        en: "Youth, seniors, intergenerational links",
        pt: "Juventude, idosos, vínculos intergeracionais"
      },
      icon: Heart,
      color: '#F7C948'
    }
  ];

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1A0F2E]' : 'bg-[#FAFAFA]'}`}>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#5B2D8E] to-[#7B4AAE] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#F7C948]/10 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 lg:px-10 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm font-medium mb-4"
            >
              <Users className="w-4 h-4" />
              {content.subtitle}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-black mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {content.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-white/80"
            >
              {content.description}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Organigramme */}
      <section className="py-16">
        <div className="container mx-auto px-4 lg:px-10 max-w-6xl">
          
          {/* PRÉSIDENT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className={`inline-block p-8 rounded-2xl ${isDark ? 'bg-gradient-to-br from-[#5B2D8E] to-[#3E1A66]' : 'bg-gradient-to-br from-[#5B2D8E] to-[#7B4AAE]'} shadow-2xl`}
              style={{ boxShadow: '0 20px 60px rgba(91, 45, 142, 0.4)' }}
            >
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#F7C948] flex items-center justify-center">
                <User className="w-10 h-10 text-[#5B2D8E]" />
              </div>
              <h2 className="text-2xl font-black text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                {president.name}
              </h2>
              <p className="text-[#F7C948] font-bold text-lg">{president.role[language] || president.role.fr}</p>
              <p className="text-white/70 text-sm mt-2 max-w-xs mx-auto">
                {president.desc[language] || president.desc.fr}
              </p>
            </div>
          </motion.div>

          {/* Connector */}
          <div className="flex justify-center mb-8">
            <div className={`w-0.5 h-12 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
          </div>

          {/* FIRST CIRCLE LABEL */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-6"
          >
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
              isDark ? 'bg-[#F7C948]/20 text-[#F7C948]' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
            }`}>
              {content.firstCircle}
            </span>
            <p className={`text-sm mt-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {content.firstCircleDesc}
            </p>
          </motion.div>

          {/* FIRST CIRCLE - 3 positions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid md:grid-cols-3 gap-6 mb-8"
          >
            {firstCircle.map((member, index) => {
              const Icon = member.icon;
              return (
                <div
                  key={member.id}
                  className={`relative p-6 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-lg border-2 transition-all hover:-translate-y-1 ${
                    member.isCoordinator 
                      ? 'border-[#F7C948]' 
                      : isDark ? 'border-white/10' : 'border-gray-100'
                  }`}
                >
                  {/* Reports to badge */}
                  <div className={`absolute -top-3 left-4 px-3 py-1 rounded-full text-xs font-medium ${
                    isDark ? 'bg-[#1A0F2E] text-white/60' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {content.reportsTo} {member.reportsTo === 'president' ? (language === 'fr' ? 'Président' : 'President') : 'SG'}
                  </div>
                  
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 mt-2"
                    style={{ backgroundColor: `${member.color}20` }}
                  >
                    <Icon className="w-7 h-7" style={{ color: member.color }} />
                  </div>
                  
                  {member.name ? (
                    <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                      {member.name}
                    </h3>
                  ) : (
                    <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {language === 'fr' ? 'Poste à pourvoir' : language === 'en' ? 'Position to fill' : 'Cargo a preencher'}
                    </h3>
                  )}
                  
                  <p className="font-semibold text-sm mb-2" style={{ color: member.color }}>
                    {member.role[language] || member.role.fr}
                  </p>
                  
                  <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                    {member.desc[language] || member.desc.fr}
                  </p>
                  
                  {member.isCoordinator && (
                    <div className="mt-4 pt-4 border-t border-dashed border-[#F7C948]/30">
                      <span className="text-xs font-bold text-[#F7C948] flex items-center gap-1">
                        <ChevronDown className="w-4 h-4" />
                        {content.coordinates}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </motion.div>

          {/* Connector to poles */}
          <div className="flex justify-center mb-8">
            <div className="flex flex-col items-center">
              <div className={`w-0.5 h-8 ${isDark ? 'bg-[#F7C948]/50' : 'bg-[#F7C948]'}`} />
              <div className={`w-[80%] max-w-4xl h-0.5 ${isDark ? 'bg-[#F7C948]/50' : 'bg-[#F7C948]'}`} />
            </div>
          </div>

          {/* FIVE POLES LABEL */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mb-6"
          >
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
              isDark ? 'bg-[#0D9488]/20 text-[#0D9488]' : 'bg-[#0D9488]/10 text-[#0D9488]'
            }`}>
              {content.fivePoles}
            </span>
            <p className={`text-sm mt-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {content.fivePolesDesc}
            </p>
          </motion.div>

          {/* FIVE POLES */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
          >
            {poles.map((pole, index) => {
              const Icon = pole.icon;
              return (
                <div
                  key={pole.id}
                  className={`relative p-5 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-lg border transition-all hover:-translate-y-1 hover:shadow-xl ${
                    isDark ? 'border-white/10 hover:border-white/20' : 'border-gray-100 hover:border-gray-200'
                  }`}
                >
                  {/* Connector dot */}
                  <div className={`absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full ${
                    isDark ? 'bg-[#F7C948]/50' : 'bg-[#F7C948]'
                  }`} />
                  
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 mx-auto"
                    style={{ backgroundColor: `${pole.color}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: pole.color }} />
                  </div>
                  
                  <h4 className="font-bold text-sm text-center mb-2" style={{ color: pole.color }}>
                    {pole.shortRole[language] || pole.shortRole.fr}
                  </h4>
                  
                  {pole.name ? (
                    <p className={`text-center text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                      {pole.name}
                    </p>
                  ) : (
                    <p className={`text-center text-sm mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {language === 'fr' ? 'À pourvoir' : language === 'en' ? 'To fill' : 'A preencher'}
                    </p>
                  )}
                  
                  <p className={`text-xs text-center ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {pole.desc[language] || pole.desc.fr}
                  </p>
                </div>
              );
            })}
          </motion.div>

          {/* Legend / Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className={`mt-12 p-6 rounded-xl ${isDark ? 'bg-[#2D1B4E]/50' : 'bg-gray-50'}`}
          >
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#5B2D8E]" />
                <span className={isDark ? 'text-white/70' : 'text-gray-600'}>
                  {language === 'fr' ? 'Direction & Stratégie' : language === 'en' ? 'Leadership & Strategy' : 'Direção & Estratégia'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#F7C948]" />
                <span className={isDark ? 'text-white/70' : 'text-gray-600'}>
                  {language === 'fr' ? 'Coordination & Terrain' : language === 'en' ? 'Coordination & Field' : 'Coordenação & Terreno'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#0D9488]" />
                <span className={isDark ? 'text-white/70' : 'text-gray-600'}>
                  {language === 'fr' ? 'Communication & Relations' : language === 'en' ? 'Communication & Relations' : 'Comunicação & Relações'}
                </span>
              </div>
            </div>
          </motion.div>

        </div>
      </section>
    </div>
  );
};

export default OrganigramPage;
